<template>
  <div class="container">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>人事管理</el-breadcrumb-item>
      <el-breadcrumb-item>考勤数据</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主卡：三 Tab（打卡日报 / 月度统计（日报聚合） / 打卡月报） -->
    <el-card class="main-card">
      <div slot="header" class="toolbar">
        <span class="card-header">
          考勤数据
          <span class="header-summary">月度统计由打卡日报实时聚合（离职者在册数据完整呈现）；打卡月报为企微官方核算对照；补卡审批通过后自动校准</span>
        </span>
        <span class="toolbar-tip">每日 07:30 自动采集并滚动回填前 7 天日报 · 历史补拉见「考勤配置」页</span>
      </div>

      <!-- Tab 切换 + 各 Tab 独立筛选状态 -->
      <div class="filter-row">
        <el-radio-group v-model="activeTab" size="medium" @change="handleTabChange">
          <el-radio-button label="day">打卡日报</el-radio-button>
          <el-radio-button label="stat">月度统计</el-radio-button>
          <el-radio-button label="report">打卡月报</el-radio-button>
        </el-radio-group>

        <!-- 日报筛选 -->
        <template v-if="activeTab === 'day'">
          <el-date-picker
            v-model="day.filterDateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            size="medium"
            style="width: 260px; margin-left: 10px;"
            :clearable="false"
            @change="handleFilterChange('day')"
          ></el-date-picker>
          <el-input v-model="day.filterKeyword" placeholder="姓名/userid" clearable size="medium" style="width: 130px; margin-left: 10px;" @keyup.enter.native="handleFilterChange('day')" @clear="handleFilterChange('day')"></el-input>
          <el-input v-model="day.filterDepart" placeholder="部门" clearable size="medium" style="width: 120px; margin-left: 10px;" @keyup.enter.native="handleFilterChange('day')" @clear="handleFilterChange('day')"></el-input>
          <el-checkbox v-model="day.onlyException" style="margin-left: 12px;" @change="handleFilterChange('day')">仅看异常</el-checkbox>
          <el-button type="primary" size="medium" style="margin-left: 10px;" @click="handleFilterChange('day')">查询</el-button>
          <el-button size="medium" icon="el-icon-download" style="margin-left: 10px;" :loading="day.exporting" @click="handleExport('day')">导出</el-button>
          <el-button size="medium" icon="el-icon-refresh" style="margin-left: 10px;" :loading="syncingDay" @click="handleSyncDay">{{ syncingDay ? '补拉中…' : '补拉此范围' }}</el-button>
        </template>

        <!-- 月度统计筛选（日报实时聚合，无补拉：数据随日报更新） -->
        <template v-else-if="activeTab === 'stat'">
          <el-date-picker
            v-model="stat.filterMonth"
            type="month"
            value-format="yyyy-MM"
            placeholder="选择月份"
            size="medium"
            style="width: 150px; margin-left: 10px;"
            :clearable="false"
            @change="handleFilterChange('stat')"
          ></el-date-picker>
          <el-input v-model="stat.filterKeyword" placeholder="姓名/userid" clearable size="medium" style="width: 130px; margin-left: 10px;" @keyup.enter.native="handleFilterChange('stat')" @clear="handleFilterChange('stat')"></el-input>
          <el-button type="primary" size="medium" style="margin-left: 10px;" @click="handleFilterChange('stat')">查询</el-button>
          <el-button size="medium" icon="el-icon-download" style="margin-left: 10px;" :loading="stat.exporting" @click="handleExport('stat')">导出</el-button>
        </template>

        <!-- 打卡月报筛选（企微官方核算，对照参考） -->
        <template v-else>
          <el-date-picker
            v-model="month.filterMonth"
            type="month"
            value-format="yyyy-MM"
            placeholder="选择月份（空=全部）"
            size="medium"
            style="width: 170px; margin-left: 10px;"
            clearable
            @change="handleFilterChange('report')"
          ></el-date-picker>
          <el-input v-model="month.filterKeyword" placeholder="姓名/userid" clearable size="medium" style="width: 130px; margin-left: 10px;" @keyup.enter.native="handleFilterChange('report')" @clear="handleFilterChange('report')"></el-input>
          <el-button type="primary" size="medium" style="margin-left: 10px;" @click="handleFilterChange('report')">查询</el-button>
          <el-button size="medium" icon="el-icon-download" style="margin-left: 10px;" :loading="month.exporting" @click="handleExport('report')">导出</el-button>
          <el-button size="medium" icon="el-icon-refresh" style="margin-left: 10px;" :disabled="!month.filterMonth" :loading="syncingMonth" @click="handleSyncMonth">{{ syncingMonth ? '补拉中…' : '补拉此月' }}</el-button>
        </template>

        <span class="toolbar-tip" style="margin-left: auto;">共 {{ activeTab === 'day' ? day.total : activeTab === 'stat' ? stat.total : month.total }} 条</span>
      </div>

      <!-- 日报表格 -->
      <el-table v-if="activeTab === 'day'" v-loading="day.loading" :data="day.list" border stripe max-height="560" :row-class-name="dayRowClassName">
        <el-table-column label="#" type="index" width="50" :index="i => indexMethod('day', i)"></el-table-column>
        <el-table-column label="日期" width="115">
          <template slot-scope="scope">
            <div class="project-name">{{ scope.row.date }}</div>
            <el-tag size="mini" :type="scope.row.dayTypeText === '休息日' ? 'info' : ''" effect="plain">{{ scope.row.dayTypeText }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="姓名" width="100">
          <template slot-scope="scope">
            <div class="project-name">{{ scope.row.name }}</div>
            <div class="text-content">{{ scope.row.userid }}</div>
          </template>
        </el-table-column>
        <el-table-column label="部门" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope"><span class="text-content">{{ scope.row.departsName || '—' }}</span></template>
        </el-table-column>
        <el-table-column label="打卡次数" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.checkinCount }}</template>
        </el-table-column>
        <el-table-column label="实际/标准工时(h)" width="130" align="center">
          <template slot-scope="scope">{{ fmtHours(scope.row.regularWorkSec) }} / {{ fmtHours(scope.row.standardWorkSec) }}</template>
        </el-table-column>
        <el-table-column label="异常" width="70" align="center">
          <template slot-scope="scope">
            <span :class="scope.row.exceptionCnt > 0 ? 'exception-text' : 'muted-text'">{{ scope.row.exceptionCnt }}</span>
          </template>
        </el-table-column>
        <el-table-column label="异常摘要" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope"><span :class="scope.row.exceptionCnt > 0 ? 'exception-text' : 'muted-text'">{{ scope.row.exceptionSummary }}</span></template>
        </el-table-column>
        <el-table-column label="假勤摘要" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope"><span class="text-content">{{ scope.row.spSummary }}</span></template>
        </el-table-column>
        <el-table-column label="加班(h)" width="75" align="center">
          <template slot-scope="scope">{{ scope.row.otHours }}</template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="medium" @click="openDayDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 月度统计表格（日报实时聚合） -->
      <el-table v-else-if="activeTab === 'stat'" v-loading="stat.loading" :data="stat.list" border stripe max-height="560" :row-class-name="statRowClassName">
        <el-table-column label="#" type="index" width="50" :index="i => indexMethod('stat', i)"></el-table-column>
        <el-table-column label="姓名" width="110">
          <template slot-scope="scope">
            <div class="project-name" :class="{ 'departed-name': scope.row.departed }">{{ scope.row.name }}</div>
            <div class="text-content">{{ scope.row.userid }}</div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.departed ? 'info' : 'success'" effect="plain">{{ scope.row.departed ? '已离开' : '在册' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="部门" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope"><span class="text-content">{{ scope.row.departsName || '—' }}</span></template>
        </el-table-column>
        <el-table-column label="在册区间" width="185">
          <template slot-scope="scope"><span class="text-content">{{ scope.row.firstDate }} ~ {{ scope.row.lastDate }}</span></template>
        </el-table-column>
        <el-table-column label="应出勤(天)" width="90" align="center">
          <template slot-scope="scope">{{ scope.row.workDays }}</template>
        </el-table-column>
        <el-table-column label="正常(天)" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.regularDays }}</template>
        </el-table-column>
        <el-table-column label="异常(天)" width="80" align="center">
          <template slot-scope="scope">
            <span :class="scope.row.exceptDays > 0 ? 'exception-text' : 'muted-text'">{{ scope.row.exceptDays }}</span>
          </template>
        </el-table-column>
        <el-table-column label="休息(天)" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.restDays }}</template>
        </el-table-column>
        <el-table-column label="实际/标准工时(h)" width="130" align="center">
          <template slot-scope="scope">{{ fmtHours(scope.row.regularWorkSec) }} / {{ fmtHours(scope.row.standardWorkSec) }}</template>
        </el-table-column>
        <el-table-column label="异常摘要" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope"><span :class="scope.row.exceptDays > 0 ? 'exception-text' : 'muted-text'">{{ scope.row.exceptionSummary }}</span></template>
        </el-table-column>
        <el-table-column label="假勤摘要" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope"><span class="text-content">{{ scope.row.spSummary }}</span></template>
        </el-table-column>
        <el-table-column label="加班(h)" width="75" align="center">
          <template slot-scope="scope">{{ scope.row.otHours }}</template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="medium" @click="statDetail = scope.row; statDetailVisible = true">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 打卡月报表格（企微官方核算） -->
      <el-table v-else v-loading="month.loading" :data="month.list" border stripe max-height="560">
        <el-table-column label="#" type="index" width="50" :index="i => indexMethod('month', i)"></el-table-column>
        <el-table-column label="月份" width="90">
          <template slot-scope="scope"><span class="project-name">{{ scope.row.month }}</span></template>
        </el-table-column>
        <el-table-column label="姓名" width="100">
          <template slot-scope="scope">
            <div class="project-name">{{ scope.row.name }}</div>
            <div class="text-content">{{ scope.row.userid }}</div>
          </template>
        </el-table-column>
        <el-table-column label="部门" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope"><span class="text-content">{{ scope.row.departsName || '—' }}</span></template>
        </el-table-column>
        <el-table-column label="应出勤(天)" width="90" align="center">
          <template slot-scope="scope">{{ scope.row.workDays }}</template>
        </el-table-column>
        <el-table-column label="正常(天)" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.regularDays }}</template>
        </el-table-column>
        <el-table-column label="异常(天)" width="80" align="center">
          <template slot-scope="scope">
            <span :class="scope.row.exceptDays > 0 ? 'exception-text' : 'muted-text'">{{ scope.row.exceptDays }}</span>
          </template>
        </el-table-column>
        <el-table-column label="休息(天)" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.restDays }}</template>
        </el-table-column>
        <el-table-column label="实际/标准工时(h)" width="130" align="center">
          <template slot-scope="scope">{{ fmtHours(scope.row.regularWorkSec) }} / {{ fmtHours(scope.row.standardWorkSec) }}</template>
        </el-table-column>
        <el-table-column label="异常摘要" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope"><span :class="scope.row.exceptDays > 0 ? 'exception-text' : 'muted-text'">{{ scope.row.exceptionSummary }}</span></template>
        </el-table-column>
        <el-table-column label="假勤摘要" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope"><span class="text-content">{{ scope.row.spSummary }}</span></template>
        </el-table-column>
        <el-table-column label="加班(h)" width="75" align="center">
          <template slot-scope="scope">{{ scope.row.overworkHours }}</template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="medium" @click="openMonthDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页（三 Tab 独立） -->
      <el-pagination
        v-if="activeTab === 'day'"
        style="margin-top: 15px; text-align: right;"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="day.total"
        :current-page.sync="day.pagenum"
        :page-sizes="[10, 20, 50]"
        :page-size.sync="day.pagesize"
        @current-change="fetchList('day')"
        @size-change="handleSizeChange('day')"
      ></el-pagination>
      <el-pagination
        v-else-if="activeTab === 'stat'"
        style="margin-top: 15px; text-align: right;"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="stat.total"
        :current-page.sync="stat.pagenum"
        :page-sizes="[10, 20, 50]"
        :page-size.sync="stat.pagesize"
        @current-change="fetchList('stat')"
        @size-change="handleSizeChange('stat')"
      ></el-pagination>
      <el-pagination
        v-else
        style="margin-top: 15px; text-align: right;"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="month.total"
        :current-page.sync="month.pagenum"
        :page-sizes="[10, 20, 50]"
        :page-size.sync="month.pagesize"
        @current-change="fetchList('report')"
        @size-change="handleSizeChange('report')"
      ></el-pagination>
    </el-card>

    <!-- 日报明细弹窗：摘要 + 假勤审批 + 异常 + 假勤统计 + 加班 + 规则 -->
    <el-dialog :title="dayDetailTitle" :visible.sync="dayDetailVisible" width="860px" top="6vh">
      <template v-if="dayDetail">
        <div class="summary-row">
          <div class="summary-item"><span class="summary-label">姓名</span><span>{{ dayDetail.name }}（{{ dayDetail.userid }}）</span></div>
          <div class="summary-item"><span class="summary-label">日期</span><span>{{ dayDetail.date }} · {{ dayDetail.dayTypeText }}</span></div>
          <div class="summary-item"><span class="summary-label">打卡规则</span><span>{{ ruleName(dayDetail) }}</span></div>
          <div class="summary-item"><span class="summary-label">工时</span><span>实际 {{ fmtHours(dayDetail.regularWorkSec) }}h / 标准 {{ fmtHours(dayDetail.standardWorkSec) }}h</span></div>
        </div>

        <template v-if="dayDetail.exceptionInfos && dayDetail.exceptionInfos.length">
          <div class="section-title">异常明细（校准后状态，补卡审批通过自动更新）</div>
          <el-table :data="dayDetail.exceptionInfos" border size="small" style="margin-bottom: 14px;">
            <el-table-column label="异常类型" width="110">
              <template slot-scope="scope">{{ exceptionName(scope.row.exception) }}</template>
            </el-table-column>
            <el-table-column label="次数" width="70" align="center">
              <template slot-scope="scope">{{ scope.row.count }}</template>
            </el-table-column>
            <el-table-column label="时长" align="center">
              <template slot-scope="scope">{{ [1, 2, 4].includes(scope.row.exception) && scope.row.duration ? fmtDuration(scope.row.duration) : '—' }}</template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="dayDetail.holidayInfos && dayDetail.holidayInfos.length">
          <div class="section-title">关联假勤审批单</div>
          <el-table :data="dayDetail.holidayInfos" border size="small" style="margin-bottom: 14px;">
            <el-table-column label="审批单号" width="140">
              <template slot-scope="scope">{{ scope.row.sp_number }}</template>
            </el-table-column>
            <el-table-column label="类型时长" min-width="120">
              <template slot-scope="scope">{{ spText(scope.row.sp_title) }}</template>
            </el-table-column>
            <el-table-column label="时段" min-width="180">
              <template slot-scope="scope">{{ spText(scope.row.sp_description) }}</template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="dayDetail.spItems && dayDetail.spItems.length">
          <div class="section-title">假勤统计</div>
          <el-table :data="dayDetail.spItems" border size="small" style="margin-bottom: 14px;">
            <el-table-column label="统计项" min-width="110">
              <template slot-scope="scope">{{ scope.row.name }}</template>
            </el-table-column>
            <el-table-column label="次数" width="70" align="center">
              <template slot-scope="scope">{{ scope.row.count }}</template>
            </el-table-column>
            <el-table-column label="时长" align="center">
              <template slot-scope="scope">{{ scope.row.duration > 0 ? (scope.row.time_type === 1 ? (scope.row.duration / 3600).toFixed(1) + ' 小时' : (scope.row.duration / 86400).toFixed(2) + ' 天') : '—' }}</template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="dayDetail.otInfo && dayDetail.otInfo.ot_status !== 0">
          <div class="section-title">加班明细</div>
          <el-table :data="[dayDetail.otInfo]" border size="small">
            <el-table-column label="状态" width="90">
              <template slot-scope="scope">{{ scope.row.ot_status === 1 ? '正常' : '缺时长' }}</template>
            </el-table-column>
            <el-table-column label="加班时长" width="100" align="center">
              <template slot-scope="scope">{{ fmtDuration(scope.row.ot_duration) }}</template>
            </el-table-column>
            <el-table-column label="记调休" width="110" align="center">
              <template slot-scope="scope">{{ fmtDuration((scope.row.workday_over_as_vacation || 0) + (scope.row.restday_over_as_vacation || 0) + (scope.row.holiday_over_as_vacation || 0)) }}</template>
            </el-table-column>
            <el-table-column label="记加班费" align="center">
              <template slot-scope="scope">{{ fmtDuration((scope.row.workday_over_as_money || 0) + (scope.row.restday_over_as_money || 0) + (scope.row.holiday_over_as_money || 0)) }}</template>
            </el-table-column>
          </el-table>
        </template>

        <div v-if="noDetailBlocks" class="muted-text" style="text-align: center; padding: 24px 0;">当日无异常 / 假勤 / 加班记录</div>
      </template>
      <div slot="footer">
        <el-button type="primary" size="medium" @click="dayDetailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 月报明细弹窗：摘要 + 异常 + 假勤 + 加班 -->
    <el-dialog :title="monthDetailTitle" :visible.sync="monthDetailVisible" width="820px" top="8vh">
      <template v-if="monthDetail">
        <div class="summary-row">
          <div class="summary-item"><span class="summary-label">姓名</span><span>{{ monthDetail.name }}（{{ monthDetail.userid }}）</span></div>
          <div class="summary-item"><span class="summary-label">月份</span><span>{{ monthDetail.month }}</span></div>
          <div class="summary-item"><span class="summary-label">天数</span><span>应出勤 {{ monthDetail.workDays }} / 正常 {{ monthDetail.regularDays }} / 异常 {{ monthDetail.exceptDays }} / 休息 {{ monthDetail.restDays }}</span></div>
          <div class="summary-item"><span class="summary-label">工时</span><span>实际 {{ fmtHours(monthDetail.regularWorkSec) }}h / 标准 {{ fmtHours(monthDetail.standardWorkSec) }}h</span></div>
        </div>

        <template v-if="monthDetail.exceptionInfos && monthDetail.exceptionInfos.length">
          <div class="section-title">异常统计（周期累计）</div>
          <el-table :data="monthDetail.exceptionInfos" border size="small" style="margin-bottom: 14px;">
            <el-table-column label="异常类型" width="110">
              <template slot-scope="scope">{{ exceptionName(scope.row.exception) }}</template>
            </el-table-column>
            <el-table-column label="次数" width="80" align="center">
              <template slot-scope="scope">{{ scope.row.count }}</template>
            </el-table-column>
            <el-table-column label="时长" align="center">
              <template slot-scope="scope">{{ [1, 2, 4].includes(scope.row.exception) && scope.row.duration ? fmtDuration(scope.row.duration) : '—' }}</template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="monthDetail.spItems && monthDetail.spItems.length">
          <div class="section-title">假勤统计（周期累计）</div>
          <el-table :data="monthDetail.spItems" border size="small" style="margin-bottom: 14px;">
            <el-table-column label="统计项" min-width="110">
              <template slot-scope="scope">{{ scope.row.name }}</template>
            </el-table-column>
            <el-table-column label="次数" width="80" align="center">
              <template slot-scope="scope">{{ scope.row.count }}</template>
            </el-table-column>
            <el-table-column label="时长" align="center">
              <template slot-scope="scope">{{ scope.row.duration > 0 ? (scope.row.time_type === 1 ? (scope.row.duration / 3600).toFixed(1) + ' 小时' : (scope.row.duration / 86400).toFixed(2) + ' 天') : '—' }}</template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="monthDetail.overworkInfo">
          <div class="section-title">加班统计</div>
          <el-table :data="monthOverworkRows" border size="small">
            <el-table-column label="加班类别" width="130">
              <template slot-scope="scope">{{ scope.row.label }}</template>
            </el-table-column>
            <el-table-column label="时长" width="110" align="center">
              <template slot-scope="scope">{{ fmtDuration(scope.row.sec) }}</template>
            </el-table-column>
            <el-table-column label="记调休" width="110" align="center">
              <template slot-scope="scope">{{ fmtDuration(scope.row.vacation) }}</template>
            </el-table-column>
            <el-table-column label="记加班费" align="center">
              <template slot-scope="scope">{{ fmtDuration(scope.row.money) }}</template>
            </el-table-column>
          </el-table>
        </template>
      </template>
      <div slot="footer">
        <el-button type="primary" size="medium" @click="monthDetailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 月度统计明细弹窗（日报聚合）：摘要 + 异常 + 假勤 -->
    <el-dialog :title="statDetailTitle" :visible.sync="statDetailVisible" width="760px" top="8vh">
      <template v-if="statDetail">
        <div class="summary-row">
          <div class="summary-item"><span class="summary-label">姓名</span><span>{{ statDetail.name }}（{{ statDetail.userid }}）{{ statDetail.departed ? ' · 已离开' : '' }}</span></div>
          <div class="summary-item"><span class="summary-label">月份</span><span>{{ statDetail.month }}</span></div>
          <div class="summary-item"><span class="summary-label">在册区间</span><span>{{ statDetail.firstDate }} ~ {{ statDetail.lastDate }}</span></div>
          <div class="summary-item"><span class="summary-label">天数</span><span>应出勤 {{ statDetail.workDays }} / 正常 {{ statDetail.regularDays }} / 异常 {{ statDetail.exceptDays }} / 休息 {{ statDetail.restDays }}</span></div>
          <div class="summary-item"><span class="summary-label">工时</span><span>实际 {{ fmtHours(statDetail.regularWorkSec) }}h / 标准 {{ fmtHours(statDetail.standardWorkSec) }}h / 加班 {{ statDetail.otHours }}h</span></div>
        </div>

        <template v-if="statDetail.exceptionInfos && statDetail.exceptionInfos.length">
          <div class="section-title">异常统计（当月累计）</div>
          <el-table :data="statDetail.exceptionInfos" border size="small">
            <el-table-column label="异常类型" width="110">
              <template slot-scope="scope">{{ exceptionName(scope.row.exception) }}</template>
            </el-table-column>
            <el-table-column label="次数" width="80" align="center">
              <template slot-scope="scope">{{ scope.row.count }}</template>
            </el-table-column>
            <el-table-column label="时长" align="center">
              <template slot-scope="scope">{{ [1, 2, 4].includes(scope.row.exception) && scope.row.duration ? fmtDuration(scope.row.duration) : '—' }}</template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="statDetail.spItems && statDetail.spItems.length">
          <div class="section-title" style="margin-top: 14px;">假勤统计（当月累计）</div>
          <el-table :data="statDetail.spItems" border size="small">
            <el-table-column label="统计项" min-width="110">
              <template slot-scope="scope">{{ scope.row.name }}</template>
            </el-table-column>
            <el-table-column label="次数" width="80" align="center">
              <template slot-scope="scope">{{ scope.row.count }}</template>
            </el-table-column>
            <el-table-column label="时长" align="center">
              <template slot-scope="scope">{{ scope.row.duration > 0 ? (scope.row.time_type === 1 ? (scope.row.duration / 3600).toFixed(1) + ' 小时' : (scope.row.duration / 86400).toFixed(2) + ' 天') : '—' }}</template>
            </el-table-column>
          </el-table>
        </template>
      </template>
      <div slot="footer">
        <el-button type="primary" size="medium" @click="statDetailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'RenshiCheckinData',
  data () {
    return {
      activeTab: 'day',
      // 日报（服务端分页，各 Tab 筛选状态独立保留）
      day: {
        list: [],
        total: 0,
        loading: false,
        exporting: false,
        filterDateRange: [],
        filterKeyword: '',
        filterDepart: '',
        onlyException: false,
        pagenum: 1,
        pagesize: 20
      },
      // 月度统计（日报实时聚合）
      stat: {
        list: [],
        total: 0,
        loading: false,
        exporting: false,
        filterMonth: '',
        filterKeyword: '',
        pagenum: 1,
        pagesize: 20
      },
      // 打卡月报（企微官方核算）
      month: {
        list: [],
        total: 0,
        loading: false,
        exporting: false,
        filterMonth: '',
        filterKeyword: '',
        pagenum: 1,
        pagesize: 20
      },
      // 明细弹窗
      dayDetailVisible: false,
      dayDetail: null,
      monthDetailVisible: false,
      monthDetail: null,
      statDetailVisible: false,
      statDetail: null,
      // 就地补拉（异步受理 + 轮询，完成后自动刷新当前 Tab；需「考勤配置-同步」权限）
      syncingDay: false,
      syncingMonth: false,
      syncPollTimer: null
    }
  },
  computed: {
    dayDetailTitle () {
      return this.dayDetail ? `日报明细 · ${this.dayDetail.name} ${this.dayDetail.date}` : '日报明细'
    },
    monthDetailTitle () {
      return this.monthDetail ? `打卡月报明细 · ${this.monthDetail.name} ${this.monthDetail.month}` : '打卡月报明细'
    },
    statDetailTitle () {
      return this.statDetail ? `月度统计明细 · ${this.statDetail.name} ${this.statDetail.month}` : '月度统计明细'
    },
    /**
     * 日报弹窗无任何明细块时的空提示
     */
    noDetailBlocks () {
      if (!this.dayDetail) return false
      const d = this.dayDetail
      return !(d.exceptionInfos && d.exceptionInfos.length) &&
        !(d.holidayInfos && d.holidayInfos.length) &&
        !(d.spItems && d.spItems.filter(i => (i.count || 0) > 0 || (i.duration || 0) > 0).length) &&
        !(d.otInfo && d.otInfo.ot_status !== 0)
    },
    /**
     * 月报加班表行（工作日/休息日/节假日三行）
     */
    monthOverworkRows () {
      const o = this.monthDetail && this.monthDetail.overworkInfo
      if (!o) return []
      return [
        { label: '工作日加班', sec: o.workday_over_sec || 0, vacation: o.workdays_over_as_vacation || 0, money: o.workdays_over_as_money || 0 },
        { label: '休息日加班', sec: o.restdays_over_sec || 0, vacation: o.restdays_over_as_vacation || 0, money: o.restdays_over_as_money || 0 },
        { label: '节假日加班', sec: o.holidays_over_sec || 0, vacation: o.holidays_over_as_vacation || 0, money: o.holidays_over_as_money || 0 }
      ]
    }
  },
  created () {
    // 日报默认查最近 7 天（含今天），月度统计/打卡月报默认上月
    const fmtDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    this.day.filterDateRange = [fmtDay(new Date(Date.now() - 6 * 86400000)), fmtDay(new Date())]
    const now = new Date()
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const prevMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`
    this.stat.filterMonth = prevMonth
    this.month.filterMonth = prevMonth

    this.fetchList('day')
    this.fetchList('stat')
  },
  beforeDestroy () {
    this.stopSyncPolling()
  },
  methods: {
    /**
     * 秒 → 小时（1 位小数）
     */
    fmtHours (sec) {
      const n = Number(sec)
      if (!isFinite(n) || n <= 0) return '0.0'
      return (n / 3600).toFixed(1)
    },
    /**
     * 秒 → "X小时Y分钟"
     */
    fmtDuration (sec) {
      const n = Number(sec) || 0
      const h = Math.floor(n / 3600)
      const m = Math.round((n % 3600) / 60)
      if (h && m) return `${h}小时${m}分钟`
      if (h) return `${h}小时`
      return `${m}分钟`
    },
    /**
     * 时间格式化（东八区，YYYY-MM-DD HH:mm）——后端返回 ISO 串勿截前 10 位（UTC 时差）
     */
    fmtDateTime (v) {
      return v ? new Date(v).toLocaleString('sv-SE', { timeZone: 'Asia/Shanghai', hour12: false }).slice(0, 16) : ''
    },
    /**
     * 官方异常枚举（1迟到/2早退/3缺卡/4旷工/5地点异常/6设备异常）
     */
    exceptionName (code) {
      return ({ 1: '迟到', 2: '早退', 3: '缺卡', 4: '旷工', 5: '地点异常', 6: '设备异常' })[code] || `异常${code}`
    },
    /**
     * 多语言文本块（sp_title/sp_description 的 data[0].text）
     */
    spText (block) {
      return (block && block.data && block.data[0] && block.data[0].text) || ''
    },
    /**
     * 打卡规则名（base_info.rule_info：规则名 + 班次）
     */
    ruleName (row) {
      const r = row.baseInfo && row.baseInfo.rule_info
      if (!r) return '—'
      return r.schedulename ? `${r.groupname} · ${r.schedulename}` : (r.groupname || '—')
    },
    /**
     * 日报有异常行淡红背景
     */
    dayRowClassName ({ row }) {
      return row.exceptionCnt > 0 ? 'exception-row' : ''
    },
    /**
     * 月度统计行有异常时淡红背景
     */
    statRowClassName ({ row }) {
      return row.exceptDays > 0 ? 'exception-row' : ''
    },
    /**
     * Tab 键 → data 状态键映射（打卡月报 Tab 键为 report，状态键为 month）
     */
    _st (tab) {
      return this[tab === 'report' ? 'month' : tab]
    },
    /**
     * 分页序号（跨页连续）
     */
    indexMethod (tab, index) {
      const st = this._st(tab)
      return (st.pagenum - 1) * st.pagesize + index + 1
    },
    /**
     * 组装筛选参数（列表查询与导出共用，所见即所得）
     */
    _buildParams (tab) {
      const st = this._st(tab)
      const params = { pagenum: st.pagenum, pagesize: st.pagesize }
      if (tab === 'day') {
        const [startDate, endDate] = st.filterDateRange || []
        if (startDate) params.startDate = startDate
        if (endDate) params.endDate = endDate
        if (st.filterKeyword.trim()) params.keyword = st.filterKeyword.trim()
        if (st.filterDepart.trim()) params.depart = st.filterDepart.trim()
        if (st.onlyException) params.onlyException = true
      } else {
        // stat（月度统计）与 report（打卡月报）同为月份+关键字筛选
        params.month = st.filterMonth
        if (st.filterKeyword.trim()) params.keyword = st.filterKeyword.trim()
      }
      return params
    },
    /**
     * 加载列表（服务端分页）
     */
    async fetchList (tab) {
      const st = this._st(tab)
      st.loading = true
      try {
        const path = tab === 'day' ? 'renshi/checkin/dayList' : tab === 'stat' ? 'renshi/checkin/statMonthList' : 'renshi/checkin/monthList'
        const { data: res } = await this.$http.get(path, { params: this._buildParams(tab) })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        st.list = res.data.list || []
        st.total = res.data.total || 0
      } finally {
        st.loading = false
      }
    },
    handleTabChange (tab) {
      this.fetchList(tab)
    },
    handleFilterChange (tab) {
      this._st(tab).pagenum = 1
      this.fetchList(tab)
    },
    handleSizeChange (tab) {
      this._st(tab).pagenum = 1
      this.fetchList(tab)
    },
    /**
     * 导出（所见即所得；无权限/参数错误时后端返回 JSON 需识别，勿落盘损坏文件）
     */
    async handleExport (tab) {
      const st = this._st(tab)
      if (!st.total) return this.$message.warning('当前筛选条件下无数据可导出')
      st.exporting = true
      try {
        const path = tab === 'day' ? 'renshi/checkin/dayExport' : tab === 'stat' ? 'renshi/checkin/statMonthExport' : 'renshi/checkin/monthExport'
        const { data } = await this.$http.get(path, { params: this._buildParams(tab), responseType: 'blob' })
        if (data.type === 'application/json') {
          const err = JSON.parse(await data.text())
          return this.$message.error((err.meta && err.meta.msg) || '导出失败')
        }
        const names = { day: '考勤日报', stat: '月度统计', report: '打卡月报' }
        this._downloadBlob(data, `${names[tab]}_${this._exportTag(tab)}.xlsx`)
      } catch (err) {
        this.$message.error('导出失败')
      } finally {
        st.exporting = false
      }
    },
    _exportTag (tab) {
      const st = this._st(tab)
      if (tab !== 'day') return st.filterMonth || '全部'
      const [start, end] = st.filterDateRange || []
      if (start && end) return start === end ? String(start) : `${start}_${end}`
      return '全部'
    },
    /**
     * 通用的 Blob 文件下载方法
     */
    _downloadBlob (blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    /**
     * 就地补拉：日报按当前日期范围发起（发起后参数即固定；需「考勤配置-同步」权限）
     */
    async handleSyncDay () {
      const [startDate, endDate] = this.day.filterDateRange || []
      if (!startDate || !endDate) return this.$message.warning('请先选择日期范围')
      this.syncingDay = true
      try {
        const { data: res } = await this.$http.post('renshi/checkin/syncDay', { startDate, endDate })
        if (res.meta.status !== 200) {
          this.syncingDay = false
          return this.$message.error(res.meta.msg)
        }
        this.startSyncPolling('syncDay')
      } catch (err) {
        this.syncingDay = false
        this.$message.error('补拉请求失败')
      }
    },
    /**
     * 就地补拉：打卡月报按当前选中月份
     */
    async handleSyncMonth () {
      if (!this.month.filterMonth) return this.$message.warning('请先选择月份')
      this.syncingMonth = true
      try {
        const { data: res } = await this.$http.post('renshi/checkin/syncMonth', { month: this.month.filterMonth })
        if (res.meta.status !== 200) {
          this.syncingMonth = false
          return this.$message.error(res.meta.msg)
        }
        this.startSyncPolling('syncMonth')
      } catch (err) {
        this.syncingMonth = false
        this.$message.error('补拉请求失败')
      }
    },
    /**
     * 轮询补拉状态（5s；非 running 即完成）：按结果提示并刷新当前 Tab；
     * 轮询接口无权限或异常时降级为 12s 后直接刷新
     */
    startSyncPolling (taskKey) {
      this.stopSyncPolling()
      this.syncPollTimer = setInterval(async () => {
        try {
          const { data: res } = await this.$http.get('renshi/checkin/syncStatus')
          if (res.meta.status !== 200) {
            this.stopSyncPolling()
            setTimeout(() => this.finishSync(taskKey, null), 12000)
            return
          }
          const state = res.data.tasks && res.data.tasks[taskKey]
          if (state && state.status !== 'running') {
            this.stopSyncPolling()
            this.finishSync(taskKey, state)
            return
          }
          // 兜底：后发起的轮询顶掉先前的（syncDay/syncMonth 共用一个定时器），
          // 若被顶掉任务此时已结束则复位其 loading，防按钮永久卡在"补拉中…"
          const t = res.data.tasks || {}
          if (t.syncDay && t.syncDay.status !== 'running') this.syncingDay = false
          if (t.syncMonth && t.syncMonth.status !== 'running') this.syncingMonth = false
        } catch (err) {
          this.stopSyncPolling()
          setTimeout(() => this.finishSync(taskKey, null), 12000)
        }
      }, 5000)
    },
    stopSyncPolling () {
      if (this.syncPollTimer) {
        clearInterval(this.syncPollTimer)
        this.syncPollTimer = null
      }
    },
    /**
     * 补拉收尾：提示结果（成功带统计）并刷新当前 Tab 列表
     */
    finishSync (taskKey, state) {
      if (taskKey === 'syncDay') this.syncingDay = false
      else this.syncingMonth = false
      if (state && state.status === 'success') {
        const s = state.stat || {}
        const msg = taskKey === 'syncDay'
          ? `补拉完成：${s.dateRange || ''} · 日报 ${s.dayUpserted || 0} 条`
          : `补拉完成：${s.month || ''} · 打卡月报 ${s.upserted || 0} 条`
        this.$message.success(msg)
      } else if (state && state.status === 'error') {
        this.$message.error(`补拉失败：${state.message}`)
      } else {
        this.$message.info('补拉已提交，已为您刷新最新数据')
      }
      this.fetchList(this.activeTab)
    },
    /**
     * 日报明细弹窗（行数据已含全部原样块，无需再调接口）
     */
    openDayDetail (row) {
      this.dayDetail = row
      this.dayDetailVisible = true
    },
    /**
     * 月报明细弹窗
     */
    openMonthDetail (row) {
      this.monthDetail = row
      this.monthDetailVisible = true
    }
  }
}
</script>

<style scoped>
.main-card {
  margin-top: 20px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header {
  font-size: 16px;
  font-weight: bold;
}
.header-summary {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 12px;
}

.filter-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-top: 12px;
  border-top: 1px solid #EBEEF5;
  flex-wrap: wrap;
  row-gap: 8px;
}

.toolbar-tip {
  color: #909399;
  font-size: 12px;
}

.text-content {
  font-size: 12px;
  color: #909399;
}
.project-name {
  font-size: 13px;
  color: #303133;
}
.muted-text {
  color: #c0c4cc;
}
.exception-text {
  color: #F56C6C;
  font-size: 12px;
}
::v-deep .exception-row {
  background: #FEF6F6 !important;
}

/* 明细弹窗 */
.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #F5F7FA;
  border-radius: 4px;
  font-size: 13px;
  color: #303133;
}
.summary-item .summary-label {
  color: #909399;
  margin-right: 8px;
}
.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin: 4px 0 8px;
}
</style>
